/**
 * The rule language in the browser — the twin of backend/app/data/logic.py.
 *
 * Rules are checked here for instant feedback and again on the server, which is the
 * authority. The two evaluators must agree exactly: both run the cases in
 * backend/app/data/logic_cases.json (`node scripts/logic-conformance.ts`). This file
 * imports nothing so that script can load it directly.
 */

export type Rule = unknown
export type RuleData = Record<string, unknown>

export const OPERATORS = [
  'var', '==', '===', '!=', '!==', '>', '>=', '<', '<=', 'and', 'or', '!', '!!', 'if',
  '+', '-', '*', '/', '%', 'min', 'max', 'round', 'ceil', 'floor', 'cat', 'in', 'today', 'now', 'me',
  'days',
] as const

let todayOverride: string | null = null
// The organisation's time zone: "today" and "now" are its, as on the server.
let timeZone: string | undefined
/** The auth provider sets it from /api/auth/me. */
export function setTimeZone(name: string | null | undefined) {
  timeZone = name || undefined
}
let nowOverride: string | null = null
export interface Me {
  id: string
  email: string
  name: string
}
let meValue: Me | null = null
/** The signed-in person, for {"me": "email"}; the auth provider sets it. */
export function setMe(value: Me | null) {
  meValue = value
}
/** For the conformance cases, which fix "now" (a time of day, "HH:MM"). */
export function setNow(value: string | null) {
  nowOverride = value
}
/** For the conformance cases, which fix "today". */
export function setToday(value: string | null) {
  todayOverride = value
}

export function truthy(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0
  return Boolean(value) && value !== ''
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return 0
  if (typeof value === 'boolean') return value ? 1 : 0
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function looseEqual(a: unknown, b: unknown): boolean {
  if (a === null || a === undefined || b === null || b === undefined) {
    return (a === null || a === undefined) && (b === null || b === undefined)
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') return a === b
  if ((typeof a === 'number' && typeof b === 'string') || (typeof b === 'number' && typeof a === 'string')) {
    const na = toNumber(a)
    const nb = toNumber(b)
    return na !== null && nb !== null && na === nb
  }
  return JSON.stringify(a) === JSON.stringify(b)
}

function compare(op: string, a: unknown, b: unknown): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false
  let left: number | string
  let right: number | string
  if (typeof a === 'string' && typeof b === 'string') {
    left = a
    right = b
  } else {
    const na = toNumber(a)
    const nb = toNumber(b)
    if (na === null || nb === null) return false
    left = na
    right = nb
  }
  if (op === '>') return left > right
  if (op === '>=') return left >= right
  if (op === '<') return left < right
  return left <= right
}

function readVar(path: unknown, data: RuleData): unknown {
  if (path === null || path === undefined || path === '') return data
  let current: unknown = data
  for (const part of String(path).split('.')) {
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      current = (current as RuleData)[part]
    } else {
      return null
    }
  }
  // A linked record is loaded when something reads through it ("product.name"), but
  // the link itself still means its id — so it can be compared or copied to a link.
  if (current && typeof current === 'object' && !Array.isArray(current) && 'id' in current) {
    return (current as RuleData).id
  }
  return current === undefined ? null : current
}

function text(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

/** Whole results stay whole; float noise from * and / is trimmed. */
function clean(n: number): number {
  return Number.isInteger(n) ? n : Number(n.toPrecision(15))
}

/** The zone's wall clock now: year, month, day, hour and minute. */
function clock(): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date())
  return Object.fromEntries(parts.map((p) => [p.type, p.value]))
}

function todayIso(): string {
  if (todayOverride) return todayOverride
  const now = clock()
  return `${now.year}-${now.month}-${now.day}`
}

/** A date (or the date of a date and time) as days since 1970, or null. */
function dayNumber(value: unknown): number | null {
  if (typeof value !== 'string' || value.length < 10) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!m) return null
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const back = new Date(ms)
  if (back.getUTCMonth() !== Number(m[2]) - 1 || back.getUTCDate() !== Number(m[3])) return null
  return ms / 86_400_000
}

/** The time of day as "HH:MM", in the organisation's zone. */
function nowHHMM(): string {
  if (nowOverride) return nowOverride
  const now = clock()
  return `${now.hour}:${now.minute}`
}

export function evaluate(rule: Rule, data: RuleData = {}): unknown {
  if (Array.isArray(rule)) return rule.map((item) => evaluate(item, data))
  if (rule === null || typeof rule !== 'object') return rule
  const entries = Object.entries(rule as Record<string, unknown>)
  if (entries.length !== 1) throw new Error('Each rule step is an object with exactly one operator')
  const [op, raw] = entries[0]
  if (!(OPERATORS as readonly string[]).includes(op)) throw new Error(`Unknown rule operator ${op}`)
  const args = Array.isArray(raw) ? raw : [raw]

  if (op === 'var') {
    const found = readVar(args.length ? evaluate(args[0], data) : null, data)
    if (found === null && args.length > 1) return evaluate(args[1], data)
    return found
  }
  if (op === 'and') {
    let value: unknown = null
    for (const item of args) {
      value = evaluate(item, data)
      if (!truthy(value)) return value
    }
    return value
  }
  if (op === 'or') {
    let value: unknown = null
    for (const item of args) {
      value = evaluate(item, data)
      if (truthy(value)) return value
    }
    return value
  }
  if (op === 'if') {
    const rest = [...args]
    while (rest.length >= 2) {
      const condition = rest.shift()
      const value = rest.shift()
      if (truthy(evaluate(condition, data))) return evaluate(value, data)
    }
    return rest.length ? evaluate(rest[0], data) : null
  }

  const values = args.map((item) => evaluate(item, data))
  switch (op) {
    case 'today':
      return todayIso()
    case 'now':
      return nowHHMM()
    case 'days': {
      const start = dayNumber(values[0])
      const end = dayNumber(values[1])
      return start === null || end === null ? null : end - start
    }
    case 'me': {
      const key = (values[0] as string) || 'id'
      if (key !== 'id' && key !== 'email' && key !== 'name') {
        throw new Error('me takes "id", "email" or "name"')
      }
      return meValue?.[key] || null
    }
    case '==':
      return looseEqual(values[0], values[1])
    case '===':
      return JSON.stringify(values[0] ?? null) === JSON.stringify(values[1] ?? null)
    case '!=':
      return !looseEqual(values[0], values[1])
    case '!==':
      return JSON.stringify(values[0] ?? null) !== JSON.stringify(values[1] ?? null)
    case '>':
    case '>=':
    case '<':
    case '<=':
      if (values.length === 3 && (op === '<' || op === '<=')) {
        return compare(op, values[0], values[1]) && compare(op, values[1], values[2])
      }
      return compare(op, values[0], values[1])
    case '!':
      return !truthy(values[0] ?? null)
    case '!!':
      return truthy(values[0] ?? null)
    case 'cat':
      return values.map((v) => (v === null || v === undefined ? '' : text(v))).join('')
    case 'in': {
      const [needle, haystack] = values
      if (Array.isArray(haystack)) return haystack.some((item) => looseEqual(needle, item))
      if (typeof haystack === 'string' && needle !== null && needle !== undefined) {
        return haystack.includes(text(needle))
      }
      return false
    }
  }

  const numbers = values.map(toNumber)
  if (numbers.some((n) => n === null)) return null
  const ns = numbers as number[]
  switch (op) {
    case '+':
      return clean(ns.reduce((a, b) => a + b, 0))
    case '*':
      return clean(ns.reduce((a, b) => a * b, 1))
    case '-':
      return clean(ns.length === 1 ? -ns[0] : ns[0] - ns[1])
    case '/':
      return ns[1] === 0 ? null : clean(ns[0] / ns[1])
    case '%':
      return ns[1] === 0 ? null : clean(ns[0] % ns[1])
    case 'min':
      return ns.length ? clean(Math.min(...ns)) : null
    case 'max':
      return ns.length ? clean(Math.max(...ns)) : null
    case 'round': {
      const factor = 10 ** Math.trunc(ns[1] ?? 0)
      // Half away from zero — the same as the server and SQL round().
      const scaled = Number((Math.abs(ns[0]) * factor).toPrecision(12))
      const rounded = Math.floor(scaled + 0.5) / factor
      return clean(ns[0] >= 0 ? rounded : -rounded)
    }
    case 'ceil':
    case 'floor': {
      // Up or down to a whole number (or to `digits` places), float noise first — the
      // same as the server: 3390 × 0.18 is 610.2, so up is 611.
      const factor = 10 ** Math.trunc(ns[1] ?? 0)
      const scaled = Number((ns[0] * factor).toPrecision(12))
      return clean((op === 'ceil' ? Math.ceil(scaled) : Math.floor(scaled)) / factor)
    }
  }
  throw new Error(`Unknown rule operator ${op}`)
}

/** Every `var` path a rule reads. */
export function variables(rule: Rule): Set<string> {
  const found = new Set<string>()
  const walk = (node: unknown) => {
    if (Array.isArray(node)) node.forEach(walk)
    else if (node && typeof node === 'object') {
      for (const [op, raw] of Object.entries(node as Record<string, unknown>)) {
        if (op === 'var') {
          const name = Array.isArray(raw) ? raw[0] : raw
          if (typeof name === 'string' && name) found.add(name)
        } else walk(raw)
      }
    }
  }
  walk(rule)
  return found
}
